import type { Player } from '../../types/player';
import type { LeagueConfig } from '../../types/league';

/**
 * Best Ball League Formula
 * Emphasizes depth, weekly upside, and optimal lineup construction
 * No waivers, no trades - draft and optimal lineup is set automatically
 */

export interface BestBallFormulaWeights {
  weeklyUpside: number;        // Most important - ceiling games
  projectedPoints: number;     // Base projection
  depthValue: number;         // Value of roster depth
  byeWeekCoverage: number;    // Bye week replacement
  consistency: number;        // Less important than upside
  rookieUpside: number;       // Rookie breakout potential
  injuryReplacement: number;  // Injury hedge value
  positionScarcity: number;   // Balanced across positions
  correlationBonus: number;   // Stacking opportunities
  previousPPG: number;        // Less weight on past performance
}

export const BESTBALL_WEIGHTS: BestBallFormulaWeights = {
  weeklyUpside: 0.25,         // Highest weight - ceiling matters most
  projectedPoints: 0.18,      // Base projection
  depthValue: 0.15,          // Need depth everywhere
  byeWeekCoverage: 0.12,     // Bye weeks are crucial
  consistency: 0.08,         // Less important than standard
  rookieUpside: 0.08,        // Rookies can break out mid-season
  injuryReplacement: 0.06,   // Injury hedge
  positionScarcity: 0.04,    // Less extreme position premiums
  correlationBonus: 0.02,    // Stacking bonuses
  previousPPG: 0.02         // Minimal weight on past
};

// Best Ball roster construction targets (12-team league)
const BESTBALL_ROSTER_TARGETS = {
  QB: { min: 2, optimal: 3, max: 4 },     // Need bye week coverage
  RB: { min: 5, optimal: 7, max: 9 },     // Injury-prone position
  WR: { min: 6, optimal: 8, max: 10 },    // Most roster spots
  TE: { min: 2, optimal: 3, max: 4 },     // Bye week coverage
  K: { min: 1, optimal: 2, max: 2 },      // Bye week coverage
  DEF: { min: 2, optimal: 3, max: 4 }     // Matchup optimization
};

export function calculateBestBallValue(
  player: Player,
  leagueConfig: LeagueConfig
): number {
  const weights = BESTBALL_WEIGHTS;
  
  // Core factors
  const weeklyUpside = calculateWeeklyUpside(player);
  const projectedPoints = player.projection || player.projectedPoints || 0;
  const depthValue = calculateDepthValue(player);
  const byeWeekCoverage = calculateByeWeekValue(player);
  const consistency = calculateConsistency(player);
  const rookieUpside = calculateRookieUpside(player);
  const injuryReplacement = calculateInjuryReplacementValue(player);
  const positionScarcity = calculateBestBallScarcity(player);
  const correlationBonus = calculateCorrelationBonus(player);
  const previousPPG = (player.lastYearPoints || 0) / 17;
  
  // Calculate weighted score
  const bestBallValue = 
    (weights.weeklyUpside * weeklyUpside) +
    (weights.projectedPoints * projectedPoints) +
    (weights.depthValue * depthValue) +
    (weights.byeWeekCoverage * byeWeekCoverage) +
    (weights.consistency * consistency) +
    (weights.rookieUpside * rookieUpside) +
    (weights.injuryReplacement * injuryReplacement) +
    (weights.positionScarcity * positionScarcity) +
    (weights.correlationBonus * correlationBonus) +
    (weights.previousPPG * previousPPG);
  
  // Apply late-round value boost for depth pieces
  const lateRoundBoost = calculateLateRoundBoost(player);
  
  // Apply QB saturation penalty for Best Ball (target: only 3 QBs in top 24)
  const qbSaturationPenalty = calculateBestBallQBSaturation(player);
  
  return bestBallValue * lateRoundBoost * qbSaturationPenalty;
}

function calculateWeeklyUpside(player: Player): number {
  // Players with high week-to-week variance get boosted
  const position = player.position;
  
  // Base upside by position
  const baseUpside = {
    QB: 0.7,  // Less variance than skill positions
    RB: 0.8,  // Moderate variance
    WR: 1.0,  // Highest variance - boom/bust
    TE: 0.6,  // Lower ceiling typically
    K: 0.9,   // High variance week-to-week
    DEF: 1.0  // Very high variance
  };
  
  let upside = baseUpside[position as keyof typeof baseUpside] || 0.5;
  
  // Boost for young players (more upside potential)
  const age = player.age || 26;
  if (age <= 24) upside *= 1.15;
  else if (age <= 26) upside *= 1.05;
  
  // Boost for players in high-powered offenses
  const strongOffenses = ['BUF', 'KC', 'SF', 'DAL', 'MIA', 'CIN', 'DET'];
  if (strongOffenses.includes(player.team)) upside *= 1.1;
  
  return upside;
}

function calculateDepthValue(player: Player): number {
  // Later picks get value boost since you need depth
  const position = player.position;
  const rank = player.rank || 100;
  
  // Position-specific depth curves
  const depthCurves = {
    QB: rank > 12 ? 1.2 : 1.0,   // QB depth valuable
    RB: rank > 24 ? 1.3 : 1.0,   // RB depth very valuable  
    WR: rank > 30 ? 1.4 : 1.0,   // WR depth most valuable
    TE: rank > 12 ? 1.1 : 1.0,   // TE depth moderately valuable
    K: rank > 12 ? 1.1 : 1.0,    // Need 2+ kickers
    DEF: rank > 12 ? 1.2 : 1.0   // Need 2-3 defenses
  };
  
  return depthCurves[position as keyof typeof depthCurves] || 1.0;
}

function calculateByeWeekValue(player: Player): number {
  // Every position needs bye week coverage
  const position = player.position;
  
  // Bye week importance by position
  const byeWeekValue = {
    QB: 1.1,  // Need 2-3 QBs
    RB: 1.0,  // Many RBs anyway
    WR: 1.0,  // Many WRs anyway
    TE: 1.2,  // Need 2-3 TEs specifically
    K: 1.3,   // Need backup kicker
    DEF: 1.2  // Need backup defense
  };
  
  return byeWeekValue[position as keyof typeof byeWeekValue] || 1.0;
}

function calculateRookieUpside(player: Player): number {
  const age = player.age || 26;
  
  // Rookies get upside bonus (can emerge mid-season)
  if (age <= 22) return 1.3;  // High rookie upside
  if (age <= 24) return 1.1;  // Some upside
  return 1.0;                 // No bonus
}

function calculateInjuryReplacementValue(player: Player): number {
  const position = player.position;
  
  // Positions more likely to need injury replacements
  const injuryValue = {
    QB: 0.9,  // QBs get hurt less
    RB: 1.4,  // RBs get hurt most - need many
    WR: 1.1,  // WRs get hurt sometimes  
    TE: 1.0,  // Average
    K: 0.8,   // Kickers rarely get hurt
    DEF: 0.9  // Team defenses don't get "hurt"
  };
  
  return injuryValue[position as keyof typeof injuryValue] || 1.0;
}

function calculateBestBallScarcity(player: Player): number {
  // More balanced scarcity - need depth everywhere, MINIMAL QB premium
  const scarcity = {
    QB: 0.8,  // REDUCED - only need 2-3 QBs total
    RB: 1.4,  // High - need many
    WR: 1.5,  // Highest - need most
    TE: 1.3,  // Moderate premium
    K: 1.0,   // Neutral
    DEF: 1.1  // Slight premium for matchups
  };
  
  return scarcity[player.position as keyof typeof scarcity] || 1.0;
}

function calculateCorrelationBonus(player: Player): number {
  // QB-WR, QB-TE stacks can help in best ball
  // Mock implementation - would analyze team correlations
  const strongOffenses = ['BUF', 'KC', 'SF', 'DAL', 'MIA'];
  
  if (strongOffenses.includes(player.team)) {
    if (['QB', 'WR', 'TE'].includes(player.position)) {
      return 1.05; // Small stacking bonus
    }
  }
  
  return 1.0;
}

function calculateLateRoundBoost(player: Player): number {
  const rank = player.rank || 100;
  
  // Boost value of later picks (rounds 8+) since depth is crucial
  if (rank > 96) return 1.3;   // Round 9+ in 12-team
  if (rank > 84) return 1.2;   // Round 8
  if (rank > 72) return 1.1;   // Round 7
  return 1.0;                  // No boost for early picks
}

// Helper functions
function calculateConsistency(player: Player): number {
  // Less important in best ball - upside matters more
  const baseConsistency = { QB: 0.6, RB: 0.4, WR: 0.3, TE: 0.5, K: 0.2, DEF: 0.3 };
  return baseConsistency[player.position as keyof typeof baseConsistency] || 0.4;
}

export function getBestBallPositionPriority(): string[] {
  // Need depth everywhere, but WR/RB depth most important
  return ['RB', 'WR', 'RB', 'WR', 'QB', 'WR', 'RB', 'TE', 'WR', 'RB', 'QB', 'WR'];
}

export function getBestBallDraftStrategy(): string[] {
  return [
    'Draft 6-10 WRs - most roster spots and highest upside variance',
    'Target 5-8 RBs - injury-prone position, need depth',
    'Take 2-3 QBs - bye weeks and potential breakouts',
    'Get 2-3 TEs - bye week coverage crucial',
    'Draft 2+ defenses for weekly matchup optimization',
    'Target high-upside rookies in middle rounds',
    'Prioritize weekly ceiling over weekly floor',
    'Young players on good offenses have highest upside'
  ];
}

export function getBestBallRosterTargets() {
  return BESTBALL_ROSTER_TARGETS;
}

function calculateBestBallQBSaturation(player: Player): number {
  if (player.position !== 'QB') return 1.0;
  
  // Best Ball should have VERY few QBs in top 24 (target: 3)
  // Apply severe penalties to lower-tier QBs
  const qbRank = getQBTier(player);
  
  if (qbRank <= 3) return 1.0;      // Top 3 QBs - no penalty
  if (qbRank <= 6) return 0.5;      // QB4-6 - 50% penalty  
  if (qbRank <= 9) return 0.3;      // QB7-9 - 70% penalty
  if (qbRank <= 12) return 0.2;     // QB10-12 - 80% penalty
  return 0.1;                       // QB13+ - 90% penalty
}

function getQBTier(player: Player): number {
  // Mock QB ranking - would use real consensus rankings
  const tierOneQBs = ['Josh Allen', 'Lamar Jackson', 'Jalen Hurts'];
  const tierTwoQBs = ['Joe Burrow', 'Jayden Daniels', 'Dak Prescott', 'Tua Tagovailoa', 'Brock Purdy', 'CJ Stroud'];
  const tierThreeQBs = ['Kyler Murray', 'Aaron Rodgers', 'Trevor Lawrence', 'Jordan Love', 'Caleb Williams', 'Anthony Richardson'];
  
  if (tierOneQBs.some(name => player.name.includes(name.split(' ')[1]))) return 1;
  if (tierTwoQBs.some(name => player.name.includes(name.split(' ')[1]))) return 4;  
  if (tierThreeQBs.some(name => player.name.includes(name.split(' ')[1]))) return 8;
  
  return 15; // Default lower tier
}